import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives";

import { type CreateEventsGoalState } from "../buildRequestTemplate";

import styles from "./CreateEventsGoalForm.module.css";
import { ImplicitCollectionChip } from "./ImplicitCollectionChip";
import { ImplicitDatasetChip } from "./ImplicitDatasetChip";
import {
  type CreateEventsGoalErrors,
  MAX_EVENT_VOCABULARY,
  MAX_TAG_VOCABULARY,
  type VocabRowError,
} from "./validation";

interface Props {
  goal: CreateEventsGoalState;
  errors: CreateEventsGoalErrors;
  onChange: (next: CreateEventsGoalState) => void;
  /** When false, the row trash buttons, the "Add" buttons, and the collection
   *  checkbox read as a static view. The inputs themselves are disabled by the
   *  surrounding ``<fieldset>`` in ``AgentEditor``; this prop only removes the
   *  edit-only affordances. */
  isEditing: boolean;
}

interface VocabRow {
  key: string;
  description: string;
}

function inputClass(hasError: boolean): string {
  return `${styles.input} ${hasError ? styles.inputError : ""}`;
}

function ErrorList({ errors }: { errors: VocabRowError[] }) {
  return (
    <>
      {errors.map((err) => (
        <div key={`${err.field}-${err.message}`} className={styles.error}>
          {err.message}
        </div>
      ))}
    </>
  );
}

/**
 * Editor for one of the create-events goal's two vocabularies. Each row pairs
 * a key (an event-type name / a tag) with a description; rows are stored as an
 * array so they reorder and React keys stay stable as the key text is edited.
 * The component is vocabulary-agnostic — ``CreateEventsGoalForm`` maps the
 * goal's ``name`` / ``tag`` fields onto the generic ``key`` field.
 */
function VocabularyEditor({
  title,
  requiredMark,
  optionalMark,
  help,
  rows,
  errors,
  keyPlaceholder,
  descriptionPlaceholder,
  ariaPrefix,
  emptyText,
  addLabel,
  max,
  isEditing,
  onChange,
}: {
  title: string;
  /** When true, render a red ``*`` after the label. The vocabulary's
   *  at-least-one validator already raises a row-level error message
   *  that screen readers pick up; the mark is purely a visual cue. */
  requiredMark?: boolean;
  /** When true, render a muted ``(optional)`` tag after the label.
   *  Mutually exclusive with ``requiredMark`` in practice. */
  optionalMark?: boolean;
  help: React.ReactNode;
  rows: VocabRow[];
  errors: VocabRowError[];
  keyPlaceholder: string;
  descriptionPlaceholder: string;
  ariaPrefix: string;
  emptyText: string;
  addLabel: string;
  max: number;
  isEditing: boolean;
  onChange: (rows: VocabRow[]) => void;
}) {
  const goalLevelErrors = errors.filter((e) => e.index === -1);

  const updateRow = (idx: number, patch: Partial<VocabRow>) => {
    onChange(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx));
  };
  const addRow = () => {
    onChange([...rows, { key: "", description: "" }]);
  };

  return (
    <div className={styles.field}>
      <label className={styles.label}>
        {title}
        {requiredMark && (
          <span aria-hidden="true" className={styles.required}>
            *
          </span>
        )}
        {optionalMark && <span className={styles.optional}>(optional)</span>}
      </label>
      <p className={styles.help}>{help}</p>
      {rows.length === 0 ? (
        <div className={styles.empty}>{emptyText}</div>
      ) : (
        <ul className={styles.rows}>
          {rows.map((row, idx) => {
            const rowErrors = errors.filter((e) => e.index === idx);
            const keyErrors = rowErrors.filter((e) => e.field === "key");
            const descErrors = rowErrors.filter(
              (e) => e.field === "description",
            );
            const humanIndex = idx + 1;
            return (
              <li key={idx} className={styles.row}>
                <div className={styles.rowInputs}>
                  <div className={styles.cell}>
                    <input
                      aria-label={`${ariaPrefix} ${humanIndex}`}
                      className={inputClass(keyErrors.length > 0)}
                      value={row.key}
                      placeholder={keyPlaceholder}
                      onChange={(e) => updateRow(idx, { key: e.target.value })}
                    />
                    <ErrorList errors={keyErrors} />
                  </div>
                  <div className={styles.cell}>
                    <input
                      aria-label={`${ariaPrefix} ${humanIndex} description`}
                      className={inputClass(descErrors.length > 0)}
                      value={row.description}
                      placeholder={descriptionPlaceholder}
                      onChange={(e) =>
                        updateRow(idx, { description: e.target.value })
                      }
                    />
                    <ErrorList errors={descErrors} />
                  </div>
                  <div className={styles.rowActions}>
                    {isEditing && (
                      <button
                        type="button"
                        aria-label={`remove ${ariaPrefix} ${humanIndex}`}
                        className={styles.removeButton}
                        onClick={() => removeRow(idx)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {isEditing && (
        <div className={styles.addRow}>
          <Button size="small" onClick={addRow} disabled={rows.length >= max}>
            {addLabel}
          </Button>
          <span className={styles.countHint}>{rows.length}</span>
        </div>
      )}
      <ErrorList errors={goalLevelErrors} />
    </div>
  );
}

/**
 * First-class form for ``CreateEventsGoal``.
 *
 * The dataset (investigated, and what created events attach to) is bound at
 * launch time, so it shows as a read-only chip. The operator-facing inputs are
 * the event-type vocabulary (required — the agent may only create events of
 * these kinds), an optional tag vocabulary (the agent picks a subset per
 * event), an optional collection toggle (add created events to a launch-time
 * collection), and an optional natural-language focus hint.
 */
export function CreateEventsGoalForm({
  goal,
  errors,
  onChange,
  isEditing,
}: Props) {
  const focusFieldId = "event-focus-hint";
  const focusHelpId = `${focusFieldId}-help`;
  const focusErrorId = `${focusFieldId}-error`;
  const focusHasErrors = errors.event_focus_prompt.length > 0;

  return (
    <div className={styles.container}>
      <ImplicitDatasetChip />

      <VocabularyEditor
        title="Event types"
        requiredMark
        help={
          <>
            The kinds of event the agent may create, each with a description of
            what it captures. The agent can only create events of these types.
            You can reference variables with <code>{"{{name}}"}</code> in
            descriptions.
          </>
        }
        rows={goal.event_vocabulary.map((row) => ({
          key: row.name,
          description: row.description,
        }))}
        errors={errors.event_vocabulary}
        keyPlaceholder="Hard brake"
        descriptionPlaceholder="A rapid, sustained decrease in vehicle speed."
        ariaPrefix="event type"
        emptyText="No event types yet. Add at least one."
        addLabel="Add event type"
        max={MAX_EVENT_VOCABULARY}
        isEditing={isEditing}
        onChange={(rows) =>
          onChange({
            ...goal,
            event_vocabulary: rows.map((row) => ({
              name: row.key,
              description: row.description,
            })),
          })
        }
      />

      <VocabularyEditor
        title="Tags"
        optionalMark
        help={
          <>
            Tags the agent may attach to created events; the description tells
            the model when each tag applies. For every event it creates the
            agent picks the subset that fits. Leave empty to create events
            without tags.
          </>
        }
        rows={goal.tag_vocabulary.map((row) => ({
          key: row.tag,
          description: row.description,
        }))}
        errors={errors.tag_vocabulary}
        keyPlaceholder="needs-review"
        descriptionPlaceholder="Apply when the event warrants a human look."
        ariaPrefix="tag"
        emptyText="No tags. Created events will be untagged."
        addLabel="Add tag"
        max={MAX_TAG_VOCABULARY}
        isEditing={isEditing}
        onChange={(rows) =>
          onChange({
            ...goal,
            tag_vocabulary: rows.map((row) => ({
              tag: row.key,
              description: row.description,
            })),
          })
        }
      />

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={goal.collection_enabled}
            onChange={(e) =>
              onChange({ ...goal, collection_enabled: e.target.checked })
            }
          />
          Add created events to a collection
        </label>
        <p className={styles.help}>
          When enabled, the launch page asks for an event collection and every
          created event is added to it. When off, events are created on the
          dataset but not added to any collection.
        </p>
        {goal.collection_enabled && <ImplicitCollectionChip />}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={focusFieldId}>
          Event focus
          <span className={styles.optional}>(optional)</span>
        </label>
        <p id={focusHelpId} className={styles.help}>
          Extra natural-language guidance layered on top of the vocabularies
          &mdash; e.g. &ldquo;only flag intervals longer than five
          seconds.&rdquo; You can reference variables with{" "}
          <code>{"{{name}}"}</code>.
        </p>
        <textarea
          id={focusFieldId}
          className={`${styles.textarea} ${focusHasErrors ? styles.inputError : ""}`}
          rows={4}
          value={goal.event_focus_prompt}
          onChange={(e) =>
            onChange({ ...goal, event_focus_prompt: e.target.value })
          }
          placeholder="Only flag intervals the on-board QA pass did not already catch."
          aria-invalid={focusHasErrors}
          aria-describedby={
            focusHasErrors ? `${focusHelpId} ${focusErrorId}` : focusHelpId
          }
        />
        {focusHasErrors && (
          <div id={focusErrorId} role="alert">
            {errors.event_focus_prompt.map((message) => (
              <div key={message} className={styles.error}>
                {message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
